import { ScreenHeader } from "@/components/common/ScreenHeader";
import { ThemedText } from "@/components/themed-text";
import { BrandButton } from "@/components/ui/brand-button";
import { Loader } from "@/components/ui/loader";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { useColorScheme } from "@/hooks/use-color-scheme";
import { getApiErrorMessage } from "@/lib/api";
import Ionicons from "@react-native-vector-icons/ionicons";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "expo-router";
import React from "react";
import {
  FlatList,
  RefreshControl,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import paymentService, { Payment } from "../../lib/services/payment";

export default function PaymentScreen() {
  const router = useRouter();
  const { theme } = useAuth();
  const isDark = useColorScheme() === "dark";
  const {
    data,
    isLoading: loading,
    isRefetching: refreshing,
    error: loadError,
    refetch,
  } = useQuery({
    queryKey: ["payments", "list"],
    queryFn: () => paymentService.getPayments(),
  });
  const payments = data?.data ?? [];
  const error = loadError
    ? getApiErrorMessage(loadError, "Failed to load payments")
    : null;

  const onRefresh = () => {
    refetch();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateString?: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "captured":
        return BrandColors.success;
      case "authorized":
        return BrandColors.warning;
      case "pending":
        return BrandColors.info;
      case "refunded":
        return BrandColors.error;
      default:
        return BrandColors.mediumGray;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "captured":
        return "Paid";
      case "authorized":
        return "Authorized";
      case "pending":
        return "Pending";
      case "refunded":
        return "Refunded";
      default:
        return status;
    }
  };

  const renderPaymentItem = ({ item }: { item: Payment }) => {
    const amount =
      typeof item.amount === "string"
        ? parseFloat(item.amount)
        : item.amount || 0;
    const tipAmount =
      typeof item.tip_amount === "string"
        ? parseFloat(item.tip_amount)
        : item.tip_amount || 0;

    return (
      <TouchableOpacity
        style={[
          styles.paymentCard,
          {
            backgroundColor: isDark ? "#1B2436" : BrandColors.white,
            borderColor: isDark ? theme.border : "rgba(0,0,0,0.03)",
          },
        ]}
        onPress={() => router.push(`/bookings/${item.booking_id}`)}
        activeOpacity={0.9}
      >
        <View style={styles.paymentHeader}>
          <View style={styles.paymentInfo}>
            <ThemedText
              style={[styles.paymentAmount, { color: theme.text }]}
              type="subtitle"
            >
              £{amount.toFixed(2)}
            </ThemedText>
            {tipAmount > 0 && (
              <ThemedText style={styles.tipAmount} type="extraSmallSemiBold">
                + £{tipAmount.toFixed(2)} tip
              </ThemedText>
            )}
          </View>
          <View
            style={[
              styles.statusBadge,
              { backgroundColor: getStatusColor(item.status) + "15" },
            ]}
          >
            <ThemedText
              style={[
                styles.statusText,
                { color: getStatusColor(item.status) },
              ]}
              type="extraSmallSemiBold"
            >
              {getStatusLabel(item.status).toUpperCase()}
            </ThemedText>
          </View>
          {item.payment_method === "cash" && (
            <View style={styles.cashBadge}>
              <Ionicons name="cash" size={14} color="#FF9500" />
              <ThemedText
                style={styles.cashBadgeText}
                type="extraSmallSemiBold"
              >
                CASH PAYMENT
              </ThemedText>
            </View>
          )}
        </View>

        <View style={[styles.divider, { backgroundColor: theme.border }]} />

        {item.booking && (
          <View style={styles.bookingInfo}>
            <View style={styles.routeSection}>
              <View style={styles.routeIconColumn}>
                <View style={styles.pickupIconOuter}>
                  <View style={styles.pickupIconInner} />
                </View>
                {item.booking.to_address ? (
                  <View style={styles.routeDashedLine}>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <View key={index} style={styles.routeDash} />
                    ))}
                  </View>
                ) : null}
                {item.booking.to_address ? (
                  <Ionicons name="location" size={18} color="#E11D48" />
                ) : null}
              </View>

              <View style={styles.routeDetails}>
                <View style={styles.routeStop}>
                  <ThemedText style={styles.pickupLabel} type="extraSmall">
                    Pick Up
                  </ThemedText>
                  <ThemedText
                    style={[styles.routeAddress, { color: theme.text }]}
                    type="smallSemiBold"
                    numberOfLines={2}
                  >
                    {item.booking.from_address}
                  </ThemedText>
                </View>

                {item.booking.to_address ? (
                  <View style={styles.routeStop}>
                    <ThemedText
                      style={styles.destinationLabel}
                      type="extraSmall"
                    >
                      Destination
                    </ThemedText>
                    <ThemedText
                      style={[styles.routeAddress, { color: theme.text }]}
                      type="smallSemiBold"
                      numberOfLines={2}
                    >
                      {item.booking.to_address}
                    </ThemedText>
                  </View>
                ) : null}
              </View>
            </View>

            {item.driver && (
              <View style={styles.bookingRow}>
                <View
                  style={[
                    styles.iconContainer,
                    { backgroundColor: BrandColors.charcoal + "10" },
                  ]}
                >
                  <Ionicons
                    name="person"
                    size={14}
                    color={BrandColors.charcoal}
                  />
                </View>
                <ThemedText
                  style={[styles.bookingText, { color: theme.text }]}
                  type="small"
                >
                  Driver: {item.driver.name}
                </ThemedText>
              </View>
            )}
          </View>
        )}

        <View style={styles.paymentFooter}>
          <ThemedText style={styles.dateText} type="extraSmall">
            {item.captured_at
              ? `${formatDate(item.captured_at)} • ${formatTime(item.captured_at)}`
              : item.authorized_at
                ? `${formatDate(item.authorized_at)}`
                : `${formatDate(item.created_at)}`}
          </ThemedText>
          <Ionicons
            name="chevron-forward"
            size={16}
            color={BrandColors.mediumGray}
          />
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.background }]}
      edges={["top"]}
    >
      <ScreenHeader
        title="Payments"
        subtitle="Transaction History"
        style={{ marginBottom: 8 }}
      />

      {loading ? (
        <Loader inline />
      ) : error ? (
        <View style={styles.centerContent}>
          <ThemedText style={styles.emptySubtext} type="small">
            {error}
          </ThemedText>
          <BrandButton onPress={() => refetch()} style={{ marginTop: 16 }}>
            Retry
          </BrandButton>
        </View>
      ) : (
        <FlatList
          data={payments}
          renderItem={renderPaymentItem}
          keyExtractor={(item) => item.id.toString()}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={BrandColors.primary}
            />
          }
          ListEmptyComponent={
            <View style={styles.emptyState}>
              <View style={styles.emptyIconContainer}>
                <Ionicons
                  name="card-outline"
                  size={48}
                  color={BrandColors.primary}
                />
              </View>
              <ThemedText style={styles.emptyText} type="subtitle">
                No payments yet
              </ThemedText>
              <ThemedText style={styles.emptySubtext} type="small">
                Your transaction history will appear here once you make a
                payment.
              </ThemedText>
            </View>
          }
        />
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: BrandColors.backgroundGray,
  },

  centerContent: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  listContent: {
    padding: 16,
    paddingTop: 0,
  },
  paymentCard: {
    backgroundColor: BrandColors.white,
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.03)",
  },
  paymentHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
  },
  paymentInfo: {
    flex: 1,
  },
  paymentAmount: {
    fontSize: 20,
    fontWeight: "700",
    color: BrandColors.charcoal,
  },
  tipAmount: {
    fontSize: 12,
    color: BrandColors.success,
    fontWeight: "600",
    marginTop: 2,
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
  },
  statusText: {
    fontSize: 11,
    fontWeight: "700",
    letterSpacing: 0.5,
  },
  divider: {
    height: 1,
    backgroundColor: BrandColors.lightGray,
    opacity: 0.5,
    marginBottom: 12,
  },
  bookingInfo: {
    gap: 12,
    marginBottom: 12,
  },
  routeSection: {
    flexDirection: "row",
    gap: 12,
  },
  routeIconColumn: {
    alignItems: "center",
    paddingTop: 2,
  },
  pickupIconOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: BrandColors.primary,
    alignItems: "center",
    justifyContent: "center",
  },
  pickupIconInner: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: BrandColors.white,
  },
  routeDashedLine: {
    alignItems: "center",
    gap: 3,
    marginVertical: 6,
  },
  routeDash: {
    width: 2,
    height: 5,
    borderRadius: 1,
    backgroundColor: BrandColors.lightGray,
  },
  routeDetails: {
    flex: 1,
    gap: 18,
  },
  routeStop: {
    gap: 4,
  },
  pickupLabel: {
    color: BrandColors.mediumGray,
    fontSize: 12,
  },
  destinationLabel: {
    color: BrandColors.mediumGray,
    fontSize: 12,
  },
  routeAddress: {
    fontSize: 14,
    fontWeight: "700",
    lineHeight: 19,
    color: BrandColors.charcoal,
  },
  bookingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  iconContainer: {
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: "center",
    alignItems: "center",
  },
  bookingText: {
    fontSize: 14,
    color: BrandColors.darkGray,
    flex: 1,
    fontWeight: "500",
  },
  paymentFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: BrandColors.lightGray + "50",
  },
  dateText: {
    fontSize: 12,
    color: BrandColors.mediumGray,
    fontWeight: "500",
  },
  emptyState: {
    alignItems: "center",
    paddingTop: 60,
  },
  emptyIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: BrandColors.primary + "10",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 16,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: "700",
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    color: BrandColors.mediumGray,
    textAlign: "center",
    maxWidth: "70%",
  },
  cashBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: "#FFF7ED",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 0.5,
    borderColor: "#FF9500",
    alignSelf: "flex-start",
    marginTop: 8,
  },
  cashBadgeText: {
    color: "#FF9500",
    fontWeight: "700",
    fontSize: 10,
  },
});
